/**
 * Voice 模块自定义图标集
 *
 * 全部为线框图标，1.5 stroke，20px 默认
 * 设计目的：与 phosphor-icons 视觉风格区分，保持极简 monochrome 调性
 */

import type { ReactNode } from 'react'

export type VoiceIconName =
  | 'home'
  | 'compass'
  | 'mic'
  | 'music'
  | 'clone'
  | 'library'
  | 'star'
  | 'heart'
  | 'search'
  | 'play'
  | 'pause'
  | 'download'
  | 'plus'
  | 'chevronRight'
  | 'chevronDown'
  | 'arrowRight'
  | 'arrowUp'
  | 'settings'
  | 'sparkles'
  | 'wand'
  | 'upload'
  | 'waveform'
  | 'user'
  | 'bell'
  | 'coin'
  | 'fire'
  | 'folder'
  | 'trash'
  | 'close'
  | 'refresh'
  | 'headphones'
  | 'globe'
  | 'crown'
  | 'folderOpen'
  | 'moreVertical'
  | 'useVoice'
  | 'alert'

const PATHS: Record<VoiceIconName, ReactNode> = {
  home: <path d="M3 11l9-7 9 7v9a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2v-9z" />,
  compass: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M15.5 8.5l-2 5-5 2 2-5 5-2z" />
    </>
  ),
  mic: (
    <>
      <rect height="12" rx="3" width="6" x="9" y="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
    </>
  ),
  music: (
    <>
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </>
  ),
  clone: (
    <>
      <rect height="12" rx="2" width="12" x="8" y="8" />
      <path d="M4 16V6a2 2 0 0 1 2-2h10" />
    </>
  ),
  library: <path d="M4 6h16M4 12h16M4 18h10" />,
  star: <path d="M12 3l2.7 5.5 6 .9-4.3 4.2 1 6L12 17l-5.4 2.8 1-6L3.3 9.4l6-.9L12 3z" />,
  heart: <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  play: <path d="M6 4l14 8-14 8V4z" fill="currentColor" stroke="none" />,
  pause: (
    <>
      <rect fill="currentColor" height="16" rx="1" stroke="none" width="4" x="6" y="4" />
      <rect fill="currentColor" height="16" rx="1" stroke="none" width="4" x="14" y="4" />
    </>
  ),
  download: <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />,
  plus: <path d="M12 5v14M5 12h14" />,
  chevronRight: <path d="m9 6 6 6-6 6" />,
  chevronDown: <path d="m6 9 6 6 6-6" />,
  arrowRight: <path d="M5 12h14m0 0-5-5m5 5-5 5" />,
  arrowUp: <path d="M12 19V5m0 0l-5 5m5-5l5 5" />,
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1A2 2 0 1 1 4.3 17l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.7 9a1.7 1.7 0 0 0-.3-1.8L4.3 7A2 2 0 1 1 7 4.3l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1A2 2 0 1 1 19.7 7l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>
  ),
  sparkles: (
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />
  ),
  wand: <path d="M15 4V2M15 10V8M11 6h2M17 6h2M18 13v3M3 21l9-9M11 13l2 2" />,
  upload: <path d="M12 15V3m0 0L8 7m4-4l4 4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />,
  waveform: <path d="M3 12h2M7 8v8M11 5v14M15 9v6M19 7v10M21 12h2" />,
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  bell: (
    <>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </>
  ),
  coin: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 9h4a2 2 0 1 1 0 4h-4M9 15h5" />
    </>
  ),
  fire: <path d="M12 2c1 4 5 5 5 10a5 5 0 1 1-10 0c0-3 2-4 2-7 2 1 3 4 3 4z" />,
  folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />,
  trash: (
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
  ),
  close: <path d="M6 6l12 12M18 6 6 18" />,
  refresh: <path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" />,
  headphones: (
    <>
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1v-7h2a1 1 0 0 1 1 1v4zM3 19a2 2 0 0 0 2 2h1v-7H4a1 1 0 0 0-1 1v4z" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </>
  ),
  crown: (
    <>
      <path d="M3 18l2-10 4 4 3-8 3 8 4-4 2 10z" />
      <path d="M3 21h18" />
    </>
  ),
  folderOpen: (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2" />
      <path d="m3 9 1.5 10A2 2 0 0 0 6.5 21h11a2 2 0 0 0 2-1.7L21 9H3z" />
    </>
  ),
  moreVertical: (
    <>
      <circle cx="12" cy="5" fill="currentColor" r="1.25" stroke="none" />
      <circle cx="12" cy="12" fill="currentColor" r="1.25" stroke="none" />
      <circle cx="12" cy="19" fill="currentColor" r="1.25" stroke="none" />
    </>
  ),
  useVoice: (
    <path d="M4 10v4M8 7v10M12 4v16M16 7v10M20 10v4" />
  ),
  alert: (
    <>
      <line x1="12" x2="12" y1="5" y2="14" />
      <circle cx="12" cy="18" fill="currentColor" r="1" stroke="none" />
    </>
  ),
}

interface VoiceIconProps {
  name: VoiceIconName
  size?: number
  stroke?: number
  className?: string
}

export const VoiceIcon = ({ name, size = 20, stroke = 1.5, className }: VoiceIconProps) => {
  const path = PATHS[name]
  if (!path) return null
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      height={size}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={stroke}
      viewBox="0 0 24 24"
      width={size}
    >
      {path}
    </svg>
  )
}
